import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CreateInstitution from '../institutions/CreateInstitution';
import PricingSection from './PricingSection';
import LoginPopover from '../auth/LoginPopover';
import MusterRoll from './MusterRoll';
import './LandingPage.css';

// Every module listed here ships in this build. Nothing aspirational — a visitor who buys
// on the strength of this list gets exactly what it says.
const modules = ['attendance', 'leave', 'employees', 'payroll', 'devices', 'dashboards'] as const;

const LandingPage: React.FC = () => {
    const { t } = useTranslation('landing');
    const [showCreateInstitutionModal, setShowCreateInstitutionModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [loginAnchorEl, setLoginAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<string | undefined>(undefined);

    const openCreateModal = (plan?: string) => {
        setSelectedPlan(plan);
        setShowCreateInstitutionModal(true);
    };

    const handleCreateInstitutionSuccess = (message?: string, email?: string) => {
        setShowCreateInstitutionModal(false);
        let successMsg = message || t('hero.institutionCreated');
        if (email) {
            successMsg += ` ${t('hero.credentialsSentTo', { email })}`;
        }
        setSuccessMessage(successMsg);
    };

    return (
        <div className="landing-page">
            <section className="lp-hero">
                <div className="lp-shell lp-hero-shell">
                    <div className="lp-hero-copy">
                        <p className="lp-eyebrow">{t('hero.eyebrow')}</p>
                        <h1 className="lp-hero-title">{t('hero.title')}</h1>
                        <p className="lp-hero-sub">{t('hero.sub')}</p>

                        {successMessage && (
                            <p className="lp-success" role="status">{successMessage}</p>
                        )}

                        <div className="lp-hero-actions">
                            <button
                                type="button"
                                className="lp-btn lp-btn-primary"
                                onClick={() => openCreateModal()}
                            >
                                {t('startFree')}
                            </button>
                            <button
                                type="button"
                                className="lp-btn lp-btn-ghost"
                                onClick={(e) => setLoginAnchorEl(e.currentTarget)}
                            >
                                {t('hero.login')}
                            </button>
                        </div>

                        <p className="lp-hero-note">{t('hero.note')}</p>
                    </div>

                    <div className="lp-hero-panel">
                        <MusterRoll />
                    </div>
                </div>
            </section>

            <section className="lp-modules">
                <div className="lp-shell">
                    <p className="lp-eyebrow lp-eyebrow-dark">{t('modules.eyebrow')}</p>
                    <h2 className="lp-section-title">{t('modules.title')}</h2>
                    <ul className="lp-module-grid">
                        {modules.map((module) => (
                            <li className="lp-module" key={module}>
                                <h3 className="lp-module-name">{t(`modules.items.${module}.name`)}</h3>
                                <p className="lp-module-blurb">{t(`modules.items.${module}.blurb`)}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <PricingSection onGetStarted={openCreateModal} />

            <section className="lp-cta">
                <div className="lp-shell lp-cta-shell">
                    <h2 className="lp-cta-title">{t('cta.title')}</h2>
                    <p className="lp-cta-sub">{t('cta.sub')}</p>
                    <button
                        type="button"
                        className="lp-btn lp-btn-invert"
                        onClick={() => openCreateModal()}
                    >
                        {t('startFree')}
                    </button>
                </div>
            </section>

            <footer className="lp-footer">
                <div className="lp-shell lp-footer-shell">
                    <span className="lp-footer-mark">Kervolt Attendance</span>
                    <span className="lp-footer-note">
                        {t('footer.note', { year: new Date().getFullYear() })}
                    </span>
                </div>
            </footer>

            <CreateInstitution
                isModal={true}
                selectedPlan={selectedPlan}
                open={showCreateInstitutionModal}
                onClose={() => setShowCreateInstitutionModal(false)}
                onSuccess={handleCreateInstitutionSuccess}
            />
            <LoginPopover
                anchorEl={loginAnchorEl}
                open={Boolean(loginAnchorEl)}
                onClose={() => setLoginAnchorEl(null)}
            />
        </div>
    );
};

export default LandingPage;
